import { useMemo, useState } from 'react'
import cn from 'classnames'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { Toggle } from '@forwardflow/features/campaign-flow/components/ui/primitives'
import { ITouch } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { sortTouches } from '@forwardflow/features/campaign-flow/utils/playbook'

const COL = 104
const ROW = 50
export const MAX_DAYS = 60

interface ICard {
   t: ITouch
   label: string
}

/**
 * The cadence as a timeline (Campaign Studio pattern). Days only: the hour each
 * step goes out is picked per lead from the calling heatmap.
 *
 * Collapsed: only the days with a step. Expanded: every day, drag a step
 * sideways to another day; drag past the last day, or press "More days", to
 * make the campaign longer. In both: drag up or down to change the order within
 * a day; hover a day to add a call or a text on it. The dot on a call is its
 * voicemail: click to add or remove.
 */
export const CadenceTimeline = ({
   touches,
   onChange,
   selectedId,
   onSelect,
   liveId,
   onAdd,
}: {
   touches: ITouch[]
   onChange: (next: ITouch[]) => void
   selectedId: string | null
   onSelect: (id: string) => void
   liveId?: string | null
   /** Hovering a day offers a call and a text on that day. */
   onAdd?: (day: number, action: ITouch['action']) => void
}) => {
   const [expanded, setExpanded] = useState(false)
   const [length, setLength] = useState(0)
   const [drag, setDrag] = useState<{
      id: string
      day: number
      pos: number
   } | null>(null)

   const cards = useMemo<ICard[]>(() => {
      let calls = 0
      let texts = 0
      return sortTouches(touches).map((t) => ({
         t,
         label: t.action === 'call' ? `Call ${++calls}` : `Text ${++texts}`,
      }))
   }, [touches])

   const maxDay = Math.max(...touches.map((t) => t.day), 1)
   const total = Math.min(
      MAX_DAYS,
      Math.max(length, maxDay + 2, 10, drag ? drag.day + 1 : 0)
   )
   const dayOf = (c: ICard) => (drag?.id === c.t.id ? drag.day : c.t.day)
   const activeDays = Array.from(new Set(cards.map(dayOf))).sort(
      (a, b) => a - b
   )
   const days = expanded
      ? Array.from({ length: total }, (_, i) => i + 1)
      : activeDays
   const perDay = new Map<number, ICard[]>()
   for (const c of cards)
      perDay.set(dayOf(c), [...(perDay.get(dayOf(c)) ?? []), c])
   for (const [d, list] of perDay) {
      if (drag && list.some((c) => c.t.id === drag.id)) {
         const moving = list.find((c) => c.t.id === drag.id)!
         const rest = list.filter((c) => c.t.id !== drag.id)
         rest.splice(Math.min(drag.pos, rest.length), 0, moving)
         perDay.set(d, rest)
      }
   }
   const rows = Math.max(1, ...[...perDay.values()].map((l) => l.length))
   /* One more row for the hover-to-add slot. */
   const height = (rows + (onAdd ? 1 : 0)) * ROW

   const start = (c: ICard, e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest('[data-vm]')) return
      e.preventDefault()
      const x0 = e.clientX
      const y0 = e.clientY
      const pos0 = (perDay.get(c.t.day) ?? []).findIndex(
         (x) => x.t.id === c.t.id
      )
      let moved = false
      const target = (ev: PointerEvent) => ({
         day: expanded
            ? Math.max(
                 1,
                 Math.min(
                    MAX_DAYS,
                    c.t.day + Math.round((ev.clientX - x0) / COL)
                 )
              )
            : c.t.day,
         pos: Math.max(0, pos0 + Math.round((ev.clientY - y0) / ROW)),
      })
      const onMove = (ev: PointerEvent) => {
         if (Math.abs(ev.clientX - x0) > 4 || Math.abs(ev.clientY - y0) > 4)
            moved = true
         if (moved) setDrag({ id: c.t.id, ...target(ev) })
      }
      const onUp = (ev: PointerEvent) => {
         window.removeEventListener('pointermove', onMove)
         window.removeEventListener('pointerup', onUp)
         setDrag(null)
         onSelect(c.t.id)
         if (!moved) return
         const { day, pos } = target(ev)
         if (day !== c.t.day) {
            setLength((l) => Math.max(l, day + 1))
            return onChange(
               touches.map((t) => (t.id === c.t.id ? { ...t, day } : t))
            )
         }
         if (pos === pos0) return
         /* Reorder within the day: the day's slots are handed out in the new order. */
         const list = (perDay.get(c.t.day) ?? [])
            .map((x) => x.t)
            .filter((t) => t.id !== c.t.id)
         list.splice(Math.min(pos, list.length), 0, c.t)
         const slots = list.map((t) => t.time).sort()
         const time = new Map(list.map((t, i) => [t.id, slots[i]]))
         onChange(
            touches.map((t) =>
               time.has(t.id) ? { ...t, time: time.get(t.id)! } : t
            )
         )
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
   }
   const toggleVm = (id: string) =>
      onChange(
         touches.map((t) =>
            t.id === id ? { ...t, voicemail: !t.voicemail } : t
         )
      )

   return (
      <div
         className="rounded-2xl border border-white/[0.08] bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
         data-testid="cf-timeline"
      >
         <div className="overflow-x-auto cf-scroll">
            <div
               className="relative px-4 pt-4 pb-4 flex"
               style={{ minWidth: '100%' }}
            >
               <div style={{ width: days.length * COL }} className="shrink-0">
                  {/* Day headers on an axis with a dot per day */}
                  <div className="relative h-[34px]">
                     <div className="absolute left-0 right-0 top-[26px] h-px bg-white/[0.12]" />
                     {days.map((d, i) => {
                        const gap = !expanded && i > 0 && d - days[i - 1] > 1
                        const has = perDay.has(d)
                        return (
                           <div
                              key={d}
                              className={cn(
                                 'absolute top-0 flex flex-col items-start',
                                 gap &&
                                    'before:absolute before:-left-[9px] before:top-0 before:bottom-[6px] before:border-l before:border-dashed before:border-white/[0.18]'
                              )}
                              style={{ left: i * COL, width: COL }}
                              title={
                                 gap
                                    ? `${d - days[i - 1] - 1} quiet days before`
                                    : undefined
                              }
                           >
                              <span
                                 className={cn(
                                    'pl-1 text-[11px] font-semibold uppercase tracking-[.08em]',
                                    has ? 'text-white/80' : 'text-white/30'
                                 )}
                              >
                                 Day {d}
                              </span>
                              <span
                                 className={cn(
                                    'absolute top-[22px] left-[13px] w-[9px] h-[9px] rounded-full border-2',
                                    has
                                       ? 'bg-[#9061DA] border-[#DAC2FF]/60 shadow-[0_0_0_3px_rgba(144,97,218,0.25)]'
                                       : 'bg-[#1d1e23] border-white/25'
                                 )}
                              />
                           </div>
                        )
                     })}
                  </div>

                  <div className="relative mt-2" style={{ height }}>
                     {/* Hover a day: a faint slot with two icons appears under its steps */}
                     {onAdd &&
                        !drag &&
                        days.map((d, col) => (
                           <div
                              key={`add-${d}`}
                              className="group absolute top-0"
                              style={{
                                 left: col * COL,
                                 width: COL - 12,
                                 height,
                              }}
                              data-testid={`cf-tl-day-${d}`}
                           >
                              <div
                                 className="absolute left-0 right-0 h-[40px] rounded-[10px] border border-dashed border-white/[0.14] opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center justify-center gap-2 bg-white/[0.02]"
                                 style={{
                                    top: (perDay.get(d) ?? []).length * ROW + 4,
                                 }}
                              >
                                 {(['call', 'text'] as const).map((action) => (
                                    <button
                                       key={action}
                                       type="button"
                                       onClick={() => onAdd(d, action)}
                                       title={`Add a ${action} on day ${d}`}
                                       aria-label={`Add a ${action} on day ${d}`}
                                       className={cn(
                                          'relative w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110',
                                          action === 'call'
                                             ? 'bg-[#5B8DEF]/25 text-[#9DBBFF] hover:bg-[#5B8DEF]/45'
                                             : 'bg-[#00d857]/20 text-[#5BE39A] hover:bg-[#00d857]/35'
                                       )}
                                       data-testid={`cf-tl-add-${action}-${d}`}
                                    >
                                       <FlowIcon
                                          name={
                                             action === 'call'
                                                ? 'phone'
                                                : 'chat'
                                          }
                                          size={14}
                                       />
                                       <span className="absolute -top-1 -right-1 w-[14px] h-[14px] rounded-full bg-[#202127] border border-white/20 flex items-center justify-center">
                                          <FlowIcon
                                             name="plus"
                                             size={8}
                                             strokeWidth={3}
                                          />
                                       </span>
                                    </button>
                                 ))}
                              </div>
                           </div>
                        ))}
                     {days.map((d, col) =>
                        (perDay.get(d) ?? []).map((c, row) => {
                           const isCall = c.t.action === 'call'
                           return (
                              <div
                                 key={c.t.id}
                                 role="button"
                                 tabIndex={0}
                                 onPointerDown={(e) => start(c, e)}
                                 onKeyDown={(e) =>
                                    e.key === 'Enter' && onSelect(c.t.id)
                                 }
                                 data-testid={`cf-tl-${c.t.id}`}
                                 title={`${c.label} · Day ${c.t.day}${isCall && c.t.voicemail ? ' · voicemail if no answer' : ''}`}
                                 className={cn(
                                    'absolute flex items-center gap-2 h-[40px] pl-1.5 pr-2.5 rounded-[10px] border text-xs font-semibold cursor-grab select-none touch-none transition-[box-shadow,background,border-color]',
                                    isCall
                                       ? 'border-[#5B8DEF]/30 bg-[linear-gradient(135deg,rgba(91,141,239,0.22),rgba(91,141,239,0.08))]'
                                       : 'border-[#00d857]/25 bg-[linear-gradient(135deg,rgba(0,216,87,0.18),rgba(0,216,87,0.06))]',
                                    'hover:border-white/35',
                                    selectedId === c.t.id &&
                                       'border-[#DAC2FF] shadow-[0_0_0_2px_rgba(144,97,218,0.45)]',
                                    liveId === c.t.id &&
                                       'shadow-[0_0_0_2px_#00d857]',
                                    drag?.id === c.t.id &&
                                       'z-10 cursor-grabbing scale-[1.03] shadow-[0_12px_28px_rgba(0,0,0,.5)]'
                                 )}
                                 style={{
                                    left: col * COL,
                                    top: row * ROW,
                                    width: COL - 12,
                                 }}
                              >
                                 <span
                                    className={cn(
                                       'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                                       isCall
                                          ? 'bg-[#5B8DEF]/30 text-[#BFD3FF]'
                                          : 'bg-[#00d857]/25 text-[#8AF0B6]'
                                    )}
                                 >
                                    <FlowIcon
                                       name={isCall ? 'phone' : 'chat'}
                                       size={13}
                                    />
                                 </span>
                                 <span className="truncate text-white/90">
                                    {c.label}
                                 </span>
                                 {isCall && (
                                    <button
                                       type="button"
                                       data-vm
                                       onClick={() => toggleVm(c.t.id)}
                                       title={
                                          c.t.voicemail
                                             ? 'Leaves a voicemail if not answered. Click to remove.'
                                             : 'Click to leave a voicemail if not answered'
                                       }
                                       aria-label={
                                          c.t.voicemail
                                             ? 'Remove voicemail'
                                             : 'Add voicemail'
                                       }
                                       className={cn(
                                          'absolute -top-2 -right-2 w-[20px] h-[20px] rounded-full flex items-center justify-center border transition-colors',
                                          c.t.voicemail
                                             ? 'bg-[#9061DA] border-[#DAC2FF]/60 text-white'
                                             : 'bg-[#26272d] border-white/25 text-white/60 hover:text-white'
                                       )}
                                       data-testid={`cf-tl-vm-${c.t.id}`}
                                    >
                                       <FlowIcon
                                          name={c.t.voicemail ? 'vm' : 'plus'}
                                          size={10}
                                          strokeWidth={2.6}
                                       />
                                    </button>
                                 )}
                              </div>
                           )
                        })
                     )}
                  </div>
               </div>
               {expanded && total < MAX_DAYS && (
                  <button
                     type="button"
                     onClick={() => setLength(Math.min(MAX_DAYS, total + 7))}
                     className="shrink-0 self-stretch w-[84px] ml-1 rounded-xl border border-dashed border-white/[0.18] text-[#DAC2FF] text-xs font-semibold flex flex-col items-center justify-center gap-1 hover:bg-white/[0.04]"
                     data-testid="cf-tl-more-days"
                  >
                     <FlowIcon name="plus" size={14} />7 more days
                  </button>
               )}
            </div>
         </div>
         <div className="flex flex-wrap items-center gap-3 px-3.5 py-2.5 border-t border-white/[0.06] text-xs text-[#9ca3af]">
            <button
               type="button"
               onClick={() => setExpanded(!expanded)}
               className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-white/[0.06] hover:bg-white/10 text-white font-semibold"
               data-testid="cf-tl-expand"
            >
               <FlowIcon name={expanded ? 'x' : 'plus'} size={11} />
               {expanded ? 'Only active days' : 'Show every day'}
            </button>
            <span className="flex items-center gap-1.5">
               <FlowIcon name="drag" size={12} />
               {expanded
                  ? 'Drag to another day, or past the last day to add more'
                  : 'Drag up or down to reorder'}
            </span>
            <span className="ml-auto flex items-center gap-3">
               <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#9061DA] inline-flex items-center justify-center">
                     <FlowIcon name="vm" size={8} />
                  </span>
                  voicemail
               </span>
               {onAdd && (
                  <span
                     className="flex items-center gap-1.5"
                     data-testid="cf-tl-add-hint"
                  >
                     Hover a day to add
                     <span className="w-5 h-5 rounded-full bg-[#5B8DEF]/25 text-[#9DBBFF] inline-flex items-center justify-center">
                        <FlowIcon name="phone" size={10} />
                     </span>
                     <span className="w-5 h-5 rounded-full bg-[#00d857]/20 text-[#5BE39A] inline-flex items-center justify-center">
                        <FlowIcon name="chat" size={10} />
                     </span>
                  </span>
               )}
            </span>
         </div>
      </div>
   )
}

/** Editor for the selected step, shown under the timeline. Day only; the hour comes from the heatmap. */
export const TouchDetails = ({
   touch,
   label,
   onChange,
   onRemove,
   canRemove,
}: {
   touch: ITouch
   label: string
   onChange: (t: ITouch) => void
   onRemove: () => void
   canRemove: boolean
}) => (
   <div
      className="flex flex-wrap items-center gap-3 rounded-xl bg-[#9061DA]/10 border border-[#9061DA]/30 px-4 py-2.5 text-[13px]"
      data-testid="cf-touch-details"
   >
      <b className="font-semibold w-14">{label}</b>
      <label className="flex items-center gap-2 text-[#9ca3af]">
         on day
         <input
            type="number"
            min={1}
            max={MAX_DAYS}
            value={touch.day}
            onChange={(e) =>
               onChange({
                  ...touch,
                  day: Math.max(
                     1,
                     Math.min(MAX_DAYS, Number(e.target.value) || 1)
                  ),
               })
            }
            className="h-8 w-16 rounded-lg bg-white/[0.06] border border-white/[0.12] px-2 text-white outline-none"
            data-testid="cf-touch-day"
         />
      </label>
      {touch.action === 'call' && (
         <Toggle
            on={!!touch.voicemail}
            onChange={(v) => onChange({ ...touch, voicemail: v })}
            label="Voicemail if no answer"
         />
      )}
      <button
         type="button"
         onClick={onRemove}
         disabled={!canRemove}
         className="ml-auto inline-flex items-center gap-1 text-[#9ca3af] hover:text-white disabled:opacity-30"
         data-testid="cf-touch-remove"
      >
         <FlowIcon name="trash" size={13} /> Remove
      </button>
   </div>
)
